import React, { useEffect, useRef, useState } from 'react';
import {
    Card,
    Select,
    Tabs,
    Empty,
    Spin,
    Button,
    Space,
    Typography,
    Row,
    Col,
    Badge,
    Statistic,
    Tooltip,
    Alert,
} from 'antd';
import {
    HistoryOutlined,
    RocketOutlined,
    ThunderboltOutlined,
    TeamOutlined,
    PlusOutlined,
    ExperimentOutlined,
    CalendarOutlined,
    FileTextOutlined,
    SendOutlined,
    ReloadOutlined,
    TagOutlined,
    AppstoreOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from 'store';
import { fetchApplicationsLookup, selectApplicationsLookup } from 'store/slices/applications.slice';
import {
    fetchSegments,
    fetchTemplates,
    fetchCampaigns,
    fetchTriggers,
    fetchSchedules,
    clearMessages,
    clearCampaigns,
    selectSegments,
    selectTemplates,
    selectTriggers,
    selectSchedules,
    selectCampaigns,
    selectGroupedTotal,
    fetchSegment,
} from 'store/slices/push.slice';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ROUTES } from 'config/routes';
import ABTestList from '../../components/push/ABTestList';
import { fetchABTests, selectABTests } from 'store/slices/abTest.slice';
import { usePermissions } from 'hooks/usePermissions';
import { selectCurrentUser } from 'store/slices/auth.slice';

import PushList from '../Push/PushList';
import CampaignList from '../Campaigns/CampaignList';
import EventTriggerList from '../EventTriggers/EventTriggerList';
import RecurringScheduleList from '../RecurringSchedules/RecurringScheduleList';
import SegmentList from '../Segments/SegmentList';

const STORAGE_KEY_APP_ID = 'push_center.selected_app_id';
const STORAGE_KEY_TAB = 'push_center.active_tab';

const { Title, Text } = Typography;

const PushManagementPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const { isAccManager, isViewer, isBuyer } = usePermissions();
    const currentUser = useAppSelector(selectCurrentUser);

    const applications = useAppSelector(selectApplicationsLookup);
    const segments = useAppSelector(selectSegments);
    const templates = useAppSelector(selectTemplates);
    const triggers = useAppSelector(selectTriggers);
    const schedules = useAppSelector(selectSchedules);
    const campaigns = useAppSelector(selectCampaigns);
    const historyTotal = useAppSelector(selectGroupedTotal);
    const abTests = useAppSelector(selectABTests);

    const [selectedAppId, setSelectedAppId] = useState<number | null>(() => {
        const stored = localStorage.getItem(STORAGE_KEY_APP_ID);
        return stored ? Number(stored) : null;
    });
    const [loadingResources, setLoadingResources] = useState(false);
    const [activeTab, setActiveTab] = useState<string>(() => {
        return localStorage.getItem(STORAGE_KEY_TAB) || 'history';
    });
    const [refreshing, setRefreshing] = useState(false);
    const [loadedTabs, setLoadedTabs] = useState<Set<string>>(new Set());
    const tabsAppIdRef = useRef<number | null>(null);
    const [appsLoaded, setAppsLoaded] = useState(false);
    const accManagerSegmentIds: number[] = isAccManager ? (currentUser?.segment_ids ?? []) : [];
    const hasNoSegments = isAccManager && accManagerSegmentIds.length === 0;
    const hasNoApps = !isAccManager && appsLoaded && applications.length === 0;
    const isHistoryOnly = isViewer || isBuyer;
    const [selectedSegmentId, setSelectedSegmentId] = useState<number | null>(null);

    useEffect(() => {
        if (isAccManager && accManagerSegmentIds.length > 0 && selectedSegmentId === null) {
            setSelectedSegmentId(accManagerSegmentIds[0]);
        }
    }, [isAccManager, accManagerSegmentIds.length]);

    useEffect(() => {
        if (isAccManager && selectedSegmentId !== null) {
            loadAccManagerData();
        }
    }, [selectedSegmentId]);

    useEffect(() => {
        if (isAccManager) {
            if (accManagerSegmentIds.length === 0) {
                setLoadingResources(false);
            }
            return;
        }

        dispatch(fetchApplicationsLookup())
            .unwrap()
            .then((apps) => {
                setAppsLoaded(true);
                if (apps.length === 0) return;
                const urlAppId = searchParams.get('appId');
                const urlTab = searchParams.get('tab');
                const storedAppId = localStorage.getItem(STORAGE_KEY_APP_ID);
                const storedTab = localStorage.getItem(STORAGE_KEY_TAB);
                const targetTab = isHistoryOnly ? 'history' : (urlTab || storedTab || 'history');
                const candidateId = urlAppId
                    ? Number(urlAppId)
                    : storedAppId
                        ? Number(storedAppId)
                        : null;
                const validId = candidateId && apps.some(a => a.id === candidateId)
                    ? candidateId
                    : apps[0].id;

                setActiveTab(targetTab);
                setSelectedAppId(validId);
                localStorage.setItem(STORAGE_KEY_APP_ID, String(validId));
                localStorage.setItem(STORAGE_KEY_TAB, targetTab);
                setSearchParams({ appId: String(validId), tab: targetTab }, { replace: true });
            })
            .catch((error) => {
                setAppsLoaded(true);
                console.error('Failed to fetch applications:', error);
            });
    }, [dispatch, isAccManager, isViewer, isBuyer]);

    useEffect(() => {
        if (isAccManager) return;
        if (!selectedAppId) {
            tabsAppIdRef.current = null;
            dispatch(clearMessages());
            dispatch(clearCampaigns());
            return;
        }
        if (tabsAppIdRef.current !== selectedAppId) {
            tabsAppIdRef.current = selectedAppId;
            setLoadedTabs(new Set());
            loadTabData(activeTab);
            return;
        }
        if (!loadedTabs.has(activeTab)) {
            loadTabData(activeTab);
        }
    }, [selectedAppId, activeTab]);

    const loadAccManagerData = async () => {
        if (!selectedSegmentId) return;
        setLoadingResources(true);
        try {
            await Promise.all(accManagerSegmentIds.map(id => dispatch(fetchSegment(id))));
            setLoadedTabs(new Set(['history']));
        } catch (error) {
            console.error('Failed to load acc_manager data:', error);
        } finally {
            setLoadingResources(false);
        }
    };

    const loadTabData = async (tab: string) => {
        if (!selectedAppId) return;
        const effectiveTab = isHistoryOnly ? 'history' : tab;
        setLoadingResources(true);
        try {
            const requests: Promise<any>[] = [];

            switch (effectiveTab) {
                case 'history':
                    if (!isBuyer) {
                        requests.push(dispatch(fetchSegments({ appId: selectedAppId, limit: 1000 })));
                    }
                    break;
                case 'campaigns':
                    requests.push(dispatch(fetchCampaigns({ appId: selectedAppId, limit: 1000 })));
                    break;
                case 'automation':
                    requests.push(
                        dispatch(fetchTriggers(selectedAppId)),
                        dispatch(fetchSchedules(selectedAppId)),
                        dispatch(fetchTemplates(selectedAppId)),
                        dispatch(fetchSegments({ appId: selectedAppId, limit: 1000 }))
                    );
                    break;
                case 'segments':
                    requests.push(dispatch(fetchSegments({ appId: selectedAppId, limit: 1000 })));
                    break;
                case 'templates':
                    requests.push(dispatch(fetchTemplates(selectedAppId)));
                    break;
                case 'abtests':
                    requests.push(dispatch(fetchABTests({ appId: selectedAppId, limit: 100 })));
                    break;
            }

            await Promise.all(requests);
            setLoadedTabs(prev => new Set(prev).add(effectiveTab));
        } catch (error) {
            console.error('Error loading tab data:', error);
        } finally {
            setLoadingResources(false);
        }
    };

    const loadAllResources = async () => {
        if (!selectedAppId) return;

        setLoadingResources(true);
        try {
            await Promise.all([
                dispatch(fetchSegments({ appId: selectedAppId, limit: 1000 })),
                dispatch(fetchTemplates(selectedAppId)),
            ]);
            await new Promise(resolve => setTimeout(resolve, 100));
            await Promise.all([
                dispatch(fetchCampaigns({ appId: selectedAppId, limit: 1000 })),
                dispatch(fetchTriggers(selectedAppId)),
                dispatch(fetchSchedules(selectedAppId)),
            ]);
            await new Promise(resolve => setTimeout(resolve, 100));
            await dispatch(fetchABTests({ appId: selectedAppId, limit: 100 }));
            setLoadedTabs(new Set(['history', 'campaigns', 'automation', 'segments', 'templates', 'abtests']));
        } catch (error) {
            console.error('Error loading resources:', error);
        } finally {
            setLoadingResources(false);
        }
    };

    const handleAppChange = (appId: number) => {
        setSelectedAppId(appId);
        localStorage.setItem(STORAGE_KEY_APP_ID, String(appId));
        setSearchParams({ appId: String(appId), tab: activeTab });
    };

    const handleTabChange = (key: string) => {
        setActiveTab(key);
        localStorage.setItem(STORAGE_KEY_TAB, key);
        if (!isAccManager) {
            setSearchParams({ appId: String(selectedAppId), tab: key });
        }
    };

    const handleSegmentChange = (segmentId: number) => {
        setSelectedSegmentId(segmentId);
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        if (isAccManager) {
            await loadAccManagerData();
        } else if (isHistoryOnly) {
            setLoadedTabs(new Set());
            await loadTabData('history');
        } else {
            setLoadedTabs(new Set());
            await loadAllResources();
        }
        setRefreshing(false);
    };

    const stats = {
        pushes: historyTotal,
        campaigns: campaigns.length,
        triggers: triggers.length,
        schedules: schedules.length,
        segments: segments.length,
        templates: templates.length,
        activeTriggers: triggers.filter(t => t.is_active).length,
        activeSchedules: schedules.filter(s => s.is_active).length,
        abTests: abTests.length,
        runningABTests: abTests.filter(t => t.status === 'running').length,
    };

    const historyLabel = (
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <HistoryOutlined />
            <span>История</span>
            <Badge count={stats.pushes} size="small" />
        </span>
    );

    const accManagerTabs = [
        {
            key: 'history',
            label: historyLabel,
            children: <PushList appId={null} isAccManager segmentId={selectedSegmentId} />,
        },
    ];

    const historyOnlyTabs = [
        {
            key: 'history',
            label: historyLabel,
            children: <PushList appId={selectedAppId} />,
        },
    ];

    const fullTabs = [
        {
            key: 'history',
            label: historyLabel,
            children: <PushList appId={selectedAppId} />,
        },
        {
            key: 'campaigns',
            label: (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <RocketOutlined />
                    <span>Кампании</span>
                    <Badge count={stats.campaigns} size="small" />
                </span>
            ),
            children: <CampaignList appId={selectedAppId} />,
        },
        {
            key: 'automation',
            label: (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ThunderboltOutlined />
                    <span>Автоматизация</span>
                    <Badge
                        count={stats.activeTriggers + stats.activeSchedules}
                        size="small"
                        style={{ backgroundColor: 'var(--success)' }}
                    />
                </span>
            ),
            children: (
                <div>
                    <Row gutter={[24, 24]}>
                        <Col span={24}>
                            <Card
                                title={
                                    <Space>
                                        <ThunderboltOutlined />
                                        <span>Event Triggers</span>
                                        <Text type="secondary">(События: регистрация, депозит)</Text>
                                    </Space>
                                }
                                extra={
                                    <Space>
                                        <Badge count={`${stats.activeTriggers} активных`} style={{ backgroundColor: '#52c41a' }} />
                                        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => navigate(ROUTES.EVENT_TRIGGER_CREATE)}>
                                            Создать триггер
                                        </Button>
                                    </Space>
                                }
                            >
                                <EventTriggerList appId={selectedAppId} />
                            </Card>
                        </Col>
                        <Col span={24}>
                            <Card
                                title={
                                    <Space>
                                        <CalendarOutlined />
                                        <span>Recurring Schedules</span>
                                        <Text type="secondary">(Расписания: ежедневно, еженедельно, ежемесячно)</Text>
                                    </Space>
                                }
                                extra={
                                    <Space>
                                        <Badge count={`${stats.activeSchedules} активных`} style={{ backgroundColor: '#52c41a' }} />
                                        <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => navigate(ROUTES.RECURRING_SCHEDULE_CREATE)}>
                                            Создать расписание
                                        </Button>
                                    </Space>
                                }
                            >
                                <RecurringScheduleList appId={selectedAppId} />
                            </Card>
                        </Col>
                    </Row>
                </div>
            ),
        },
        {
            key: 'segments',
            label: (
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <TeamOutlined />
                    <span>Сегменты</span>
                    <Badge count={stats.segments} size="small" />
                </span>
            ),
            children: <SegmentList appId={selectedAppId} />,
        },
        {
            key: 'abtests',
            label: (
                <span>
                    <ExperimentOutlined />
                    <span style={{ marginLeft: 8 }}>A/B тесты</span>
                    <Badge count={stats.runningABTests} style={{ marginLeft: 8, backgroundColor: 'var(--accent)' }} />
                </span>
            ),
            children: (
                <Card title={<Space><ExperimentOutlined /><span>A/B тестирование push-уведомлений</span></Space>}>
                    <ABTestList appId={selectedAppId} />
                </Card>
            ),
        },
    ];

    const tabItems = isAccManager ? accManagerTabs : isHistoryOnly ? historyOnlyTabs : fullTabs;

    const renderHeader = () => {
        if (isAccManager) {
            return (
                <div style={{ marginBottom: 24 }}>
                    <Row gutter={[16, 16]} align="middle" justify="space-between">
                        <Col xs={24} md={12}>
                            <Title level={2} style={{ margin: 0 }}>Push Management Center</Title>
                            <Text type="secondary">Просмотр истории push-уведомлений</Text>
                        </Col>
                        <Col xs={24} md={12}>
                            <Space style={{ width: '100%', justifyContent: 'flex-end' }} wrap>
                                {accManagerSegmentIds.length > 0 ? (
                                    <Select
                                        style={{ width: 450 }}
                                        size="large"
                                        placeholder="Поиск по названию сегмента..."
                                        value={selectedSegmentId}
                                        onChange={handleSegmentChange}
                                        showSearch
                                        optionFilterProp="label"
                                        filterOption={(input, option) =>
                                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                        }
                                        options={accManagerSegmentIds.map(id => {
                                            const seg = segments.find(s => s.id === id);
                                            return {
                                                value: id,
                                                label: seg
                                                    ? `${seg.name} (${seg.user_count?.toLocaleString() ?? '?'} польз.)`
                                                    : `Сегмент ID: ${id}`,
                                            };
                                        })}
                                        optionRender={(option) => {
                                            const seg = segments.find(s => s.id === option.value);
                                            return (
                                                <Space>
                                                    <TagOutlined style={{ color: 'var(--accent)' }} />
                                                    <div>
                                                        <div>{seg?.name ?? `ID: ${option.value}`}</div>
                                                        {seg?.user_count !== undefined && (
                                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                                {seg.user_count.toLocaleString()} пользователей
                                                            </Text>
                                                        )}
                                                    </div>
                                                </Space>
                                            );
                                        }}
                                    />
                                ) : null}

                                <Tooltip title="Обновить данные">
                                    <Button
                                        icon={<ReloadOutlined spin={refreshing} />}
                                        onClick={handleRefresh}
                                        loading={refreshing}
                                        size="large"
                                    />
                                </Tooltip>
                            </Space>
                        </Col>
                    </Row>

                    {hasNoSegments && (
                        <Alert
                            type="warning"
                            showIcon
                            message="Сегменты не назначены"
                            description="Обратитесь к администратору для привязки сегментов к вашему аккаунту."
                            style={{ marginTop: 16 }}
                        />
                    )}
                </div>
            );
        }
        return (
            <div style={{ marginBottom: 24 }}>
                <Row gutter={[16, 16]} align="middle" justify="space-between">
                    <Col xs={24} md={12}>
                        <Title level={2} style={{ margin: 0 }}>Push Management Center</Title>
                        {isBuyer && (
                            <Text type="secondary">Статистика по вашим пользователям</Text>
                        )}
                    </Col>
                    <Col xs={24} md={12}>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }} wrap>
                            <Select
                                style={{ width: 300 }}
                                size="large"
                                placeholder="Выберите приложение"
                                value={selectedAppId}
                                onChange={handleAppChange}
                                loading={!appsLoaded}
                                showSearch
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={applications.map(app => ({
                                    label: `${app.name} (${app.bundle_id})`,
                                    value: app.id,
                                }))}
                            />
                            <Tooltip title="Обновить данные">
                                <Button
                                    icon={<ReloadOutlined spin={refreshing} />}
                                    onClick={handleRefresh}
                                    loading={refreshing}
                                />
                            </Tooltip>
                            {!isHistoryOnly && (
                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<SendOutlined />}
                                    onClick={() => navigate(ROUTES.PUSH_SEND)}
                                >
                                    Отправить Push
                                </Button>
                            )}
                        </Space>
                    </Col>
                </Row>

                {hasNoApps && (
                    <Alert
                        type="warning"
                        showIcon
                        icon={<AppstoreOutlined />}
                        message="Приложения не назначены"
                        description="Обратитесь к администратору, чтобы вам выдали доступ к приложениям."
                        style={{ marginTop: 16 }}
                    />
                )}
            </div>
        );
    };

    const renderStats = () => {
        if (isAccManager || isHistoryOnly || !selectedAppId || loadingResources) return null;
        return (
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col xs={12} sm={8} md={4}>
                    <Card size="small">
                        <Statistic title="Push" value={stats.pushes} prefix={<SendOutlined />} />
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                    <Card size="small">
                        <Statistic title="Кампании" value={stats.campaigns} prefix={<RocketOutlined />} />
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                    <Card size="small">
                        <Statistic
                            title="Триггеры"
                            value={stats.activeTriggers}
                            suffix={`/ ${stats.triggers}`}
                            prefix={<ThunderboltOutlined />}
                            valueStyle={{ color: stats.activeTriggers > 0 ? 'var(--success)' : undefined }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                    <Card size="small">
                        <Statistic
                            title="Расписания"
                            value={stats.activeSchedules}
                            suffix={`/ ${stats.schedules}`}
                            prefix={<CalendarOutlined />}
                            valueStyle={{ color: stats.activeSchedules > 0 ? 'var(--success)' : undefined }}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                    <Card size="small">
                        <Statistic title="Сегменты" value={stats.segments} prefix={<TeamOutlined />} />
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                    <Card size="small">
                        <Statistic title="Шаблоны" value={stats.templates} prefix={<FileTextOutlined />} />
                    </Card>
                </Col>
                <Col xs={12} sm={8} md={4}>
                    <Card size="small">
                        <Statistic
                            title="A/B тесты"
                            value={stats.runningABTests}
                            suffix={`/ ${stats.abTests}`}
                            prefix={<ExperimentOutlined />}
                            valueStyle={{ color: stats.runningABTests > 0 ? 'var(--accent)' : undefined }}
                        />
                    </Card>
                </Col>
            </Row>
        );
    };

    const isContentReady = isAccManager ? true : !!selectedAppId;

    return (
        <div className="page-container">
            <Card>
                {renderHeader()}
                {renderStats()}

                {!isContentReady ? (
                    !hasNoApps && (
                        <Empty
                            description="Выберите приложение для начала работы"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    )
                ) : (
                    <Spin spinning={loadingResources} tip="Загрузка данных...">
                        <Tabs
                            activeKey={activeTab}
                            onChange={handleTabChange}
                            items={tabItems}
                            destroyInactiveTabPane={false}
                            size="large"
                        />
                    </Spin>
                )}
            </Card>
        </div>
    );
};

export default PushManagementPage;